/**
 * Krok UXR2 — PREHLED FAKTURACE podle predlohy `05-fakturace.html`:
 * tri dlazdice („K úhradě", „Po splatnosti", „Zaplaceno v <měsíci>") a
 * rychle filtry nad TIM SAMYM seznamem dokladu, ktery obrazovka uz nacita.
 *
 * Cisty vypocet nad `InvoiceDto[]` — zadny novy endpoint. Soucty nepouzivaji
 * `number`: scitaji se v celych HALERICH (castka z API je desetinny retezec),
 * aby na nich nesedela chyba binarni plovouci radove carky (CLAUDE.md §3:
 * penize nikdy jako `number`). Vysledek je zpatky retezec s dvema desetinami.
 */

import type { InvoiceDto, InvoiceStatus } from './invoices-api.js';

/**
 * Rychly filtr predlohy. `all` = vse KROME stornovanych (krok UXK3 bod 1 —
 * stornovany doklad ve vychozim pohledu plete; kdo ho chce videt, prepne na
 * `cancelled` vyslovne).
 */
export type InvoiceFilter = 'all' | 'due' | 'overdue' | 'paid' | 'draft' | 'cancelled';

export interface InvoiceBucket {
  readonly count: number;
  /** Soucet jako desetinny retezec (`"74400.00"`), nebo `null` pro prazdny kos. */
  readonly total: string | null;
  readonly currency: string | null;
}

export interface InvoiceStats {
  readonly due: InvoiceBucket & {
    /** Nejblizsi splatnost (ISO datum), nebo `null`. */
    readonly nearestDueDate: string | null;
  };
  readonly overdue: InvoiceBucket & {
    /** Nejvyssi pocet dnu po splatnosti. */
    readonly maxDaysLate: number;
  };
  readonly paidThisMonth: InvoiceBucket & {
    /** Datum posledni uhrady v tomhle mesici (ISO), nebo `null`. */
    readonly lastPaidAt: string | null;
  };
}

/**
 * Castka v halerich. Vstup je desetinny retezec z API — a POZOR, ne vzdy se
 * dvema desetinami: `finance_client_invoices` je `numeric(…, 4)`, takze API
 * posila „50820.0000". Prvni verze teto funkce brala jen dve desetiny, vsechny
 * zive castky zahodila a dlazdice ukazovaly „—" misto souctu (nalez ze
 * ZIVEHO OVERENI UXR2). Treti desetina a dal se zaokrouhluje nahoru od peti,
 * aby se soucet nerozchazel s dokladem.
 */
function toCents(amount: string): number | null {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (match === null) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const whole = Number(match[2]);
  const digits = (match[3] ?? '').padEnd(3, '0');
  const cents = whole * 100 + Number(digits.slice(0, 2));
  return sign * (Number(digits[2]) >= 5 ? cents + 1 : cents);
}

function fromCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${String(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * Soucet castek v JEDNE mene + pocet polozek. JEDINE misto, kde se v adminu
 * scitaji penize z API (v halerich, `numeric(…,4)` se zaokrouhluje nahoru od
 * peti) — krok UXK3c bod 3 si ho pro dlazdice „Ekonomiky kancelare" POUZIL,
 * misto aby si napsal druhy soucet. Vstup je zamerne uzsi nez `InvoiceDto`:
 * stejne to sedne na radek souhrnu nakladu na inzerci.
 */
export function amountBucket(
  items: readonly { readonly totalAmount: string; readonly currency: string }[],
): InvoiceBucket {
  const cents = items
    .map((invoice) => toCents(invoice.totalAmount))
    .filter((value): value is number => value !== null);
  if (cents.length === 0) return { count: items.length, total: null, currency: null };
  const currencies = new Set(items.map((invoice) => invoice.currency));
  return {
    count: items.length,
    total: fromCents(cents.reduce((sum, value) => sum + value, 0)),
    // Smisene meny se NESCITAJI do jednoho cisla — dlazdice pak menu neukaze
    // a obrazovka se musi zeptat jinak (dnes ma kancelar jednu menu).
    currency: currencies.size === 1 ? ([...currencies][0] ?? null) : null,
  };
}

/** Dnesni den jako `YYYY-MM-DD` v lokalnim case (splatnost je datum, ne okamzik). */
function isoDay(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

/** Doklad, ktery ceka na penize. Koncept ani storno se do dlazdic nepocita. */
const OPEN_STATUS: InvoiceStatus = 'issued';

export function invoiceStats(invoices: readonly InvoiceDto[], now: Date): InvoiceStats {
  const today = isoDay(now);
  const month = today.slice(0, 7);
  const open = invoices.filter((invoice) => invoice.status === OPEN_STATUS);
  const due = open.filter((invoice) => invoice.dueDate >= today);
  const overdue = open.filter((invoice) => invoice.dueDate < today);
  const paid = invoices.filter(
    (invoice) => invoice.status === 'paid' && (invoice.paidAt ?? '').slice(0, 7) === month,
  );

  const dueDates = due.map((invoice) => invoice.dueDate).sort();
  const paidDates = paid
    .map((invoice) => invoice.paidAt)
    .filter((value): value is string => value !== null)
    .sort();

  return {
    due: { ...amountBucket(due), nearestDueDate: dueDates[0] ?? null },
    overdue: {
      ...amountBucket(overdue),
      maxDaysLate: overdue.reduce((max, invoice) => Math.max(max, daysBetween(invoice.dueDate, today)), 0),
    },
    paidThisMonth: { ...amountBucket(paid), lastPaidAt: paidDates[paidDates.length - 1] ?? null },
  };
}

/** Pocty u rychlych filtru predlohy (`chip__n`). */
export function invoiceFilterCounts(
  invoices: readonly InvoiceDto[],
  now: Date,
): Readonly<Record<InvoiceFilter, number>> {
  const today = isoDay(now);
  const open = invoices.filter((invoice) => invoice.status === OPEN_STATUS);
  return {
    all: invoices.filter((invoice) => invoice.status !== 'cancelled').length,
    due: open.filter((invoice) => invoice.dueDate >= today).length,
    overdue: open.filter((invoice) => invoice.dueDate < today).length,
    paid: invoices.filter((invoice) => invoice.status === 'paid').length,
    draft: invoices.filter((invoice) => invoice.status === 'draft').length,
    cancelled: invoices.filter((invoice) => invoice.status === 'cancelled').length,
  };
}

/**
 * Rychly filtr + hledani podle cisla, odberatele NEBO PREDMETU dokladu (obe
 * nad nactenou strankou).
 *
 * Krok TOKYFIX4 bod 1: predmet se hleda proto, ze KONCEPT cislo z ucetnictvi
 * jeste nema — bez nej se prave vystavena „Provize …" v seznamu nedala najit
 * a dva koncepty tehoz odberatele vypadaly stejne (mereni P7, 19. 10.).
 */
export function filterInvoices(
  invoices: readonly InvoiceDto[],
  filter: InvoiceFilter,
  query: string,
  now: Date,
): readonly InvoiceDto[] {
  const today = isoDay(now);
  const needle = query.trim().toLocaleLowerCase('cs');
  return invoices.filter((invoice) => {
    const byFilter =
      filter === 'all'
        ? invoice.status !== 'cancelled'
        : filter === 'paid'
          ? invoice.status === 'paid'
          : filter === 'draft'
            ? invoice.status === 'draft'
            : filter === 'cancelled'
              ? invoice.status === 'cancelled'
              : invoice.status === OPEN_STATUS &&
                (filter === 'due' ? invoice.dueDate >= today : invoice.dueDate < today);
    if (!byFilter) return false;
    if (needle === '') return true;
    return (
      (invoice.myuctoNumber ?? '').toLocaleLowerCase('cs').includes(needle) ||
      invoice.customer.name.toLocaleLowerCase('cs').includes(needle) ||
      (invoice.subject ?? '').toLocaleLowerCase('cs').includes(needle)
    );
  });
}

/** Kolik dnu je doklad po splatnosti (0 = jeste neni). */
export function daysLate(invoice: InvoiceDto, now: Date): number {
  const today = isoDay(now);
  return invoice.dueDate < today ? daysBetween(invoice.dueDate, today) : 0;
}
